import {
    PlusOutlined, ShoppingCartOutlined, StarFilled, StarOutlined,
} from '@ant-design/icons';
import { zodResolver } from '@hookform/resolvers/zod';
import {
    useMutation, useQuery,
} from '@tanstack/react-query';
import {
    Button, Card, Checkbox, Flex, Form, Input, Select, Spin, Switch, theme,
    Tooltip, Upload,
    type UploadProps,
} from 'antd';
import {
    forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState,
} from 'react';
import {
    Controller, useFieldArray, useForm, useWatch,
} from 'react-hook-form';
import { type To } from 'react-router-dom';
import { z } from 'zod';

import {
    scriptsMutations, settingsQueries,
} from '@/api';
import {
    SETTING_KEY_DEFAULT_INSTRUCTIONS, UserRole,
} from '@/constants';
import { useUserStore } from '@/stores';
import {
    Script, SettingInstructionItem,
} from '@/types';

import { ConfirmModal } from './ConfirmModal';
import { EditableFieldsArray } from './EditableFieldsArray';
import {
    FormPrimaryActionsRow, FormSaveFloatButton,
} from './FormSubmitChrome';
import { TextEditor } from './TextEditor';

const instructionSchema = z.object({
    name: z.string().min(1, 'Назва розділу обов\'язкова').max(255, 'Максимум 255 символів').trim(),
    value: z.string().min(1, 'Значення розділу обов\'язкове').max(10000, 'Максимум 10000 символів'),
});

type ScriptInstructionRow = { name: string; value: string };

const cloneInstructionRows = (rows: ScriptInstructionRow[]) =>
    rows.map(({ name, value }) => ({ name, value }));

const normalizeSettingInstructionRows = (rows: unknown): ScriptInstructionRow[] => {
    if (!Array.isArray(rows)) {
        return [];
    }

    return rows.flatMap((item) => {
        if (
            !item
            || typeof item !== 'object'
            || !('instruction_name' in item)
            || !('instruction_value' in item)
        ) {
            return [];
        }

        const row = item as SettingInstructionItem;
        const name = String(row.instruction_name ?? '').trim();
        const value = String(row.instruction_value ?? '').trim();

        if (!name || !value) {
            return [];
        }

        return [{ name, value }];
    });
};

const areInstructionRowsEqual = (
    left: ScriptInstructionRow[] | null | undefined,
    right: ScriptInstructionRow[],
) => JSON.stringify(left ?? []) === JSON.stringify(right);

const defaultInstructions = [
    {
        name: 'Привітання'.toUpperCase(),
        value: '<p><span style="color: rgb(0, 0, 0); font-size: 14px;">Доброго дня, </span><em>(ім\'я клієнта)</em><span style="color: rgb(0, 0, 0); font-size: 14px;">, моє ім\'я </span><em>(ім\'я оператора),</em><span style="color: rgb(0, 0, 0); font-size: 14px;"> інтернет-магазин турбує Вас.</span><br><span style="color: rgb(0, 0, 0); font-size: 14px;">Бачимо, нам зайшла заявка на </span><em>(назва товару)</em><span style="color: rgb(0, 0, 0); font-size: 14px;">.</span><br><span style="color: rgb(0, 0, 0); font-size: 14px;">Замовляли?</span><br><br><em>(Чекаємо відповіді клієнта).</em><br><br><span style="color: rgb(0, 0, 0); font-size: 14px;">Добре.</span><br><span style="color: rgb(0, 0, 0); font-size: 14px;">Ми доставляємо Новою Поштою, термін відправки 1-2 дні, доставки 2-3 дні, оплата післяплатою, тобто вже при отриманні на пошті.</span><br></p>',
    },
    {
        name: 'Апсейл'.toUpperCase(),
        value: '<p><span style="color: rgb(0, 0, 0); font-size: 14px;">Підкажіть, яку <strong>щільність/товщину/потужність/версію </strong>вам оформити?</span></p><ul><li><p><strong>ПЕРША модель - ...... </strong>в яку входить все що заявлено на сайті.</p></li></ul><p></p><ul><li><p><strong>модель ІНШОГО виробника </strong>- .......  Як на мене - ця модель краще розвиває дрібну моторику і мовлення у дітей.</p></li></ul><p><br><span style="color: rgb(0, 0, 0); font-size: 14px;">Так, ця модель дорожче, її вартість (+....) </span><strong> грн, </strong><span style="color: rgb(0, 0, 0); font-size: 14px;">але можливо варто оформити розширену та більш якісну модель? .</span></p><p></p>',
    },
    {
        name: 'Відпрацювання заперечення апсейлу'.toUpperCase(),
        value: '<p><em>*Якщо кл відмовляється, відпрацьовуємо заперечення, давши час клієнту подумати, рекомендуємо звернути увагу на покращену версію, ще раз чітко і швидко повторюємо переваги.</em><br><br><span style="color: rgb(0, 0, 0); font-size: 14px;"><strong>(ім\'я клієнта), </strong>розумію вас, навіщо платити дорожче, якщо можна зекономити.</span></p><p><br><span style="color: rgb(0, 0, 0); font-size: 14px;">Звісно перша модель досить гарної якості і повністю відповідає заявленим характеристикам, але просто подумайте, <strong>............</strong></span><br><br><span style="color: rgb(0, 0, 0); font-size: 14px;">Плюс як я вже й сказав, в іншій моделі - буде вже <strong>........</strong></span><br><br><span style="color: rgb(0, 0, 0); font-size: 14px;">Просто оцініть все і подумайте, можливо все таки варто замінити ваше замовлення на більш розширену версію?</span></p>',
    },
    {
        name: 'Знижка на апсейл'.toUpperCase(),
        value: '<p><em>*В разі </em><strong><em>повторної відмови </em></strong><em>- пропонуємо </em><strong><em>знижку </em></strong><em>на покращену версію в розмірі</em><strong><em>&nbsp;100 грн</em></strong><br><br><span style="color: rgb(0, 0, 0); font-size: 14px;">Бачу замовляєте перший раз в нашому магазині, в такому випадку можу зробити вам привітальну знижку на більш якісну модель в розмірі</span><strong> 100 грн</strong><span style="color: rgb(0, 0, 0); font-size: 14px;">, що б не переплачували і змогли отримати кращій варіант.</span></p><p><span style="color: rgb(0, 0, 0); font-size: 14px;">Можливо, такий варіант вас влаштує? </span></p>',
    },
    {
        name: 'Кроссейл'.toUpperCase(),
        value: '<p><span style="color: rgb(0, 0, 0); font-size: 14px;">Хочу звернути вашу увагу, що даний плакат працює від 3х пальчикових батарейок. Ви можете придбати їх у нас, що б одразу на пошті могли перевірити. У нас дешевше, ніж в будь якому магазині, так як ми працюємо напряму від виробника. Вони прослужать вам не менше ніж пів року активного користування. Додати в ваше замовлення?</span><br><br><span style="color: rgb(0, 0, 0); font-size: 14px;">А) </span><strong>БАТАРЕЙКИ (2 шт) - </strong><span style="color: rgb(0, 0, 0); font-size: 14px;">ід</span><strong>&nbsp;235 -</strong><span style="color: rgb(0, 0, 0); font-size: 14px;">&nbsp;ціна </span><strong>60 грн (по 30 грн/шт) - </strong><span style="color: rgb(0, 0, 0); font-size: 14px;">на цю модель потрібно 2 упаковки (4 шт)</span><br><br><em>*Якщо кл відмовляється, відпрацьовуємо заперечення, давши час клієнту подумати, рекомендуємо купити у нас, бо у нас дешевше, ніж в магазині, кращої якості, довше тримають.</em><br><br><span style="color: rgb(0, 0, 0); font-size: 14px;">+</span><br><br><em>*також до кінця тижня діє акційна пропозиція на залишкові товари, які найчастіше беруть з вашим замовленням (розпродаж складу, економія 150-200 грн)</em><br><br><span style="color: rgb(0, 0, 0); font-size: 14px;"><strong>Б)</strong> Також можливо бачили, у нас зараз йде розпродаж складу, можемо запропонувати знижку до 300 гривень на такі товари:</span></p><ul><li><p>.....</p></li><li><p>.....</p></li></ul><p><br><span style="color: rgb(0, 0, 0); font-size: 14px;"><strong>Можливо додати його в ваше замовлення?</strong></span><br></p>',
    },
    {
        name: 'Відпрацювання на кроссейл'.toUpperCase(),
        value: '<p><em>*Якщо кл відмовляється, відпрацьовуємо заперечення, давши час клієнту подумати, рекомендуємо купити у нас, бо у нас дешевше, якщо не своїй дитині, то комусь на подарунок. Економія 300-400 грн, допродуємо залишки зі складу.</em></p><p></p><p>Так, звісно, можемо оформити без цих товарів. Моя задача вас проінформувати, бо реально зараз знижка на них 200-300 грн, так як розпродуємо залишки зі складу.</p><p><strong>Все ж подумайте, </strong>можливо варто додати в ваше замовлення? Вашій дитині буде цікавіше гратись. Або ж якщо хтось в гості прийде - гарний варіант для подарунка. </p><p>Не потрібно буде додатково платити за доставку, відправимо все однією посилкою.</p>',
    },
    {
        name: 'Доставка'.toUpperCase(),
        value: '<ul><li><p><span style="color: rgb(0, 0, 0);">Уточнили з вами всю інформацію по замовленню, тепер підкажіть -<strong> хто буде отримувач</strong>?</span></p></li></ul><p><em>*уточнюємо ПІБ клієнта</em><br></p><ul><li><p>Підкажіть<strong>, куди робимо відправку?</strong></p></li></ul><p><em>*уточнюємо місто/село і номер відділення - перевіряємо по сайту Нової Пошти і </em><strong><em>ОБОВ\'ЯЗКОВО </em></strong><em>проговорюємо адресу відділення, що б клієнт підтвердив.</em></p>',
    },
    {
        name: 'Закриття'.toUpperCase(),
        value: '<p><strong>ОТЖЕ, давайте все перевіримо:</strong></p><ul><li><p>Отримувачем буде <strong><em>(піб клієнта</em>).</strong></p></li><li><p>Відправляємо<strong> <em>(адресу доставки)</em></strong>.</p></li><li><p>В замовленні у вас - <em>(перераховуємо </em><strong><em>всі продані товари</em></strong><em>).</em></p></li></ul><p><br><span style="color: rgb(0, 0, 0); font-size: 14px;">Загальна вартість вашого замовлення - </span><em>(</em><strong><em>озвучуємо загальну вартість посилки</em></strong><em>)</em><span style="color: rgb(0, 0, 0); font-size: 14px;"> + вартість доставки нової пошти в середньому </span><strong>120-150 грн.</strong><br><br><br><strong>Дякую за замовлення!</strong><br><strong>Як тільки товар прибуде на пошту, очікуйте смс або дзвінок нашого менеджера.</strong><br><strong>Гарного дня!</strong></p>',
    },
    {
        name: 'Характеристики'.toUpperCase(),
        value: '<p>Короткі <strong>характеристики </strong>товару: </p><ul><li><p>...</p></li></ul><p></p>',
    },
];

const defaultCrossSaleInstructions = [
    {
        name: 'Характеристики'.toUpperCase(),
        value: '<p>Короткі <strong>характеристики </strong>товару: </p><ul><li><p>...</p></li></ul><p></p>',
    },
];

const SCRIPT_FORM_DOM_ID = 'script-form';

const getInstructionTemplateByType = (
    type: 'default' | 'cross-sale',
    defaultRows: ScriptInstructionRow[],
) => cloneInstructionRows(type === 'cross-sale' ? defaultCrossSaleInstructions : defaultRows);

const scriptSchema = z.object({
    title: z.string().min(1, 'Назва обов\'язкова').max(255, 'Максимум 255 символів').trim(),
    description: z.union([z.string().max(10000, 'Максимум 10000 символів'), z.null()]).optional(),
    product_price: z.union([z.string().max(255, 'Максимум 255 символів').trim(), z.string().length(0), z.null()]).optional(),
    product_sku: z.union([z.string().max(255, 'Максимум 255 символів').trim(), z.string().length(0), z.null()]).optional(),
    product_id: z.union([z.string().max(2000, 'Максимум 2000 символів').trim(), z.string().length(0), z.null()]).optional(),
    type: z.enum(['default', 'cross-sale']),
    instructions: z.array(instructionSchema).optional().nullable(),
    is_active: z.boolean(),
});

type ScriptFormData = z.infer<typeof scriptSchema>;

export type ScriptFormSubmitData = ScriptFormData & {
    gallery?: File[] | null;
    addToAllScripts: boolean;
    removeFromAllScripts: boolean;
    bulkUpdateInstructions: { name: string; value: string }[];
};

type ScriptFormProps = {
    initData?: Script | null;
    onSubmit: (data: ScriptFormSubmitData) => void;
    onCancel?: () => void;
    loading?: boolean;
    cancelButtonText?: string;
    cancelTo?: To;
    defaultType?: 'default' | 'cross-sale';
    projectId?: string;
    scriptId?: string;
    onOpenCrossSellsModal?: () => void;
    isCreating?: boolean;
};

export type ScriptFormRef = {
    reset: () => void;
    markSaved: () => void;
};

export const ScriptForm = forwardRef<ScriptFormRef, ScriptFormProps>(({
    initData,
    onSubmit,
    onCancel,
    loading = false,
    cancelButtonText,
    cancelTo,
    defaultType,
    projectId,
    scriptId,
    onOpenCrossSellsModal,
    isCreating = false,
}, ref) => {
    const [galleryFiles, setGalleryFiles] = useState<File[]>([]);
    const [deleteMediaModalOpen, setDeleteMediaModalOpen] = useState(false);
    const [mediaToDelete, setMediaToDelete] = useState<{ id: string } | null>(null);
    const [deletedGalleryIds, setDeletedGalleryIds] = useState<string[]>([]);
    const [addToAllScripts, setAddToAllScripts] = useState(false);
    const [removeFromAllScripts, setRemoveFromAllScripts] = useState(false);
    const [bulkUpdateIndices, setBulkUpdateIndices] = useState<Set<number>>(new Set());
    const lastAppliedDefaultInstructionsRef = useRef<ScriptInstructionRow[] | null>(null);
    const initDataIdRef = useRef<string | null>(null);
    const user = useUserStore((state) => state.user);

    const canReadDefaultInstructionsSetting = user?.role === UserRole.SuperAdmin
        || user?.role === UserRole.Admin
        || user?.role === UserRole.ScriptWriter;

    const { data: availableKeys } = useQuery({
        ...settingsQueries.availableKeys(),
        enabled: !initData && canReadDefaultInstructionsSetting,
    });

    const defaultInstructionsMeta = useMemo(
        () => availableKeys?.find((keyMeta) => keyMeta.key === SETTING_KEY_DEFAULT_INSTRUCTIONS),
        [availableKeys],
    );

    const { data: defaultInstructionsSetting } = useQuery({
        ...settingsQueries.getOneSetting(SETTING_KEY_DEFAULT_INSTRUCTIONS),
        enabled: !initData && canReadDefaultInstructionsSetting && Boolean(defaultInstructionsMeta?.created),
    });

    const settingDefaultInstructions = useMemo(
        () => normalizeSettingInstructionRows(defaultInstructionsSetting?.content),
        [defaultInstructionsSetting?.content],
    );

    const resolvedDefaultInstructions = useMemo(
        () => (settingDefaultInstructions.length > 0 ? settingDefaultInstructions : defaultInstructions),
        [settingDefaultInstructions],
    );

    const hasImageChanges = useMemo(() => {
        return galleryFiles.length > 0;
    }, [galleryFiles]);

    const { token } = theme.useToken();
    const hiddenSubmitRef = useRef<HTMLButtonElement>(null);

    const defaultValues = useMemo<ScriptFormData>(() => ({
        title: initData?.title || '',
        description: initData?.description || null,
        product_price: initData?.product_price || '',
        product_sku: initData?.product_sku || '',
        product_id: initData?.product_id || null,
        type: initData?.type || defaultType || 'default',
        instructions: initData
            ? (initData.instructions as Array<{ name: string; value: string }> | undefined) || []
            : getInstructionTemplateByType(defaultType || 'default', resolvedDefaultInstructions),
        is_active: initData ? Boolean(initData.is_active ?? true) : true,
    }), [initData, defaultType, resolvedDefaultInstructions]);

    const {
        control,
        getValues,
        handleSubmit,
        reset,
        formState: { errors, isDirty },
    } = useForm<ScriptFormData>({
        resolver: zodResolver(scriptSchema),
        defaultValues,
        mode: 'onChange',
    });

    const { fields, append: appendField, remove: removeField, replace } = useFieldArray({
        control,
        name: 'instructions',
    });

    const userModifiedInstructionsRef = useRef(false);

    const append = useCallback((...args: Parameters<typeof appendField>) => {
        userModifiedInstructionsRef.current = true;
        return appendField(...args);
    }, [appendField]);

    const remove = useCallback((...args: Parameters<typeof removeField>) => {
        userModifiedInstructionsRef.current = true;
        return removeField(...args);
    }, [removeField]);

    const watchedType = useWatch({ control, name: 'type' });
    const typeSyncRef = useRef<'default' | 'cross-sale' | null>(null);
    const resolvedDefaultInstructionsRef = useRef(resolvedDefaultInstructions);
    resolvedDefaultInstructionsRef.current = resolvedDefaultInstructions;

    useEffect(() => {
        if (initData) {
            if (initDataIdRef.current === initData.id) return;
            initDataIdRef.current = initData.id;
            const newDefaultValues = {
                title: initData.title || '',
                description: initData.description || null,
                product_price: initData.product_price || '',
                product_sku: initData.product_sku || '',
                product_id: initData.product_id || '',
                type: initData.type || defaultType || 'default',
                instructions: (initData.instructions as Array<{ name: string; value: string }> | undefined) || [],
                is_active: Boolean(initData.is_active ?? true),
            };
            reset(newDefaultValues, { keepDirty: false });
            typeSyncRef.current = newDefaultValues.type;
            userModifiedInstructionsRef.current = false;
            setGalleryFiles([]);
            setDeletedGalleryIds([]);
            setAddToAllScripts(false);
            setRemoveFromAllScripts(false);
            setBulkUpdateIndices(new Set());
        }
    }, [initData, reset, defaultType]);

    const primaryDisabled = loading || (!!initData && !isDirty && !hasImageChanges && !addToAllScripts && !removeFromAllScripts && bulkUpdateIndices.size === 0);
    const submitLabel = initData ? 'Оновити' : 'Створити';

    useEffect(() => {
        if (initData || userModifiedInstructionsRef.current) return;
        const t = watchedType;
        if (typeSyncRef.current === null) {
            typeSyncRef.current = t;
            return;
        }
        if (typeSyncRef.current === t) return;
        typeSyncRef.current = t;
        userModifiedInstructionsRef.current = false;
        const nextInstructions = getInstructionTemplateByType(t, resolvedDefaultInstructionsRef.current);

        replace(nextInstructions);

        if (t === 'default') {
            lastAppliedDefaultInstructionsRef.current = nextInstructions;
        }
    }, [watchedType, initData, replace]);

    const handleLoadDefaultInstructions = () => {
        const current: ScriptInstructionRow[] = getValues('instructions') || [];
        const currentNames = new Set(current.map((i) => i.name));
        const toAdd = resolvedDefaultInstructions.filter((i) => !currentNames.has(i.name));
        toAdd.forEach((i) => append({ name: i.name, value: i.value } as any));
    };

    useImperativeHandle(ref, () => ({
        reset: () => {
            reset(defaultValues);
        },
        markSaved: () => {
            initDataIdRef.current = null;
            userModifiedInstructionsRef.current = false;
        },
    }), [defaultValues, reset]);

    const handleFormSubmit = (data: ScriptFormData) => {
        const instructions = data.instructions ?? [];

        const bulkUpdateInstructions = [...bulkUpdateIndices]
            .map((i) => instructions[i])
            .filter((item): item is { name: string; value: string } => !!item?.name);

        const submitData: ScriptFormSubmitData = {
            ...data,
            product_id: data.product_id && data.product_id.trim() ? data.product_id.trim() : null,
            instructions: instructions.length > 0 ? instructions : null,
            gallery: galleryFiles.length > 0 ? galleryFiles : null,
            addToAllScripts,
            removeFromAllScripts,
            bulkUpdateInstructions,
        };
        onSubmit(submitData);
    };

    const deleteMediaMutationOptions = projectId && scriptId
        ? scriptsMutations.deleteMedia(projectId, scriptId)
        : {
            mutationKey: ['scripts', 'scripts:delete-media'],
            mutationFn: async () => {},
        };

    const { mutate: deleteMedia, isPending: isDeletingMedia } = useMutation(deleteMediaMutationOptions);

    const setMainImageMutationOptions = projectId && scriptId
        ? scriptsMutations.setMainImage(projectId, scriptId)
        : {
            mutationKey: ['scripts', 'scripts:set-main-image'],
            mutationFn: async (_mediaId: string) => {},
        };

    const { mutate: setMainImageMutate, variables: settingMainId } = useMutation(setMainImageMutationOptions);

    const handleSetMain = (mediaId: string) => {
        setMainImageMutate(mediaId);
    };
    const uploadInstructionImageMutationOptions = projectId && scriptId && initData
        ? scriptsMutations.uploadInstructionImage(projectId, scriptId)
        : {
            mutationKey: ['scripts', 'scripts:upload-instruction-image-disabled'] as const,
            mutationFn: async () => {
                throw new Error('Instruction image upload is available only for existing scripts');
            },
        };
    const { mutateAsync: uploadInstructionImage } = useMutation(uploadInstructionImageMutationOptions);

    const handleInstructionImageUpload = async (file: File): Promise<{ url: string }> => {
        const response = await uploadInstructionImage(file);

        return {
            url: response.url,
        };
    };

    const handleConfirmDeleteMedia = () => {
        if (mediaToDelete && projectId && scriptId) {
            deleteMedia(mediaToDelete.id, {
                onSuccess: () => {
                    setDeleteMediaModalOpen(false);
                    setDeletedGalleryIds((prev) => [...prev, mediaToDelete.id]);
                    setMediaToDelete(null);
                },
            });
        }
    };

    const handleCancelDeleteMedia = () => {
        setDeleteMediaModalOpen(false);
        setMediaToDelete(null);
    };

    const handleGalleryChange: UploadProps['onChange'] = (info) => {
        const fileList: File[] = [];

        info.fileList.forEach((file) => {
            if (file.originFileObj instanceof File) {
                fileList.push(file.originFileObj);
            }
        });

        setGalleryFiles(fileList);
    };

    const handleGalleryRemove = (file: any) => {
        const uid = String(file.uid || '');
        if (uid && !uid.startsWith('-') && projectId && scriptId) {
            setMediaToDelete({ id: uid });
            setDeleteMediaModalOpen(true);
        } else {
            const index = parseInt(uid.replace('-', ''), 10);
            setGalleryFiles((prev) => prev.filter((_, i) => i !== index));
        }
    };

    return (
        <Spin spinning={loading}>
            <Form
                id={SCRIPT_FORM_DOM_ID}
                layout="vertical"
                onFinish={handleSubmit(handleFormSubmit)}
            >
                <FormPrimaryActionsRow
                    onCancel={onCancel}
                    cancelButtonText={cancelButtonText}
                    cancelTo={cancelTo}
                    loading={loading}
                    submitDisabled={primaryDisabled}
                    submitLabel={submitLabel}
                    style={{ marginBottom: 24 }}
                />
                <Controller
                    name="title"
                    control={control}
                    render={({ field }) => (
                        <Form.Item
                            label="Назва"
                            required
                            validateStatus={errors.title ? 'error' : ''}
                            help={errors.title?.message}
                        >
                            <Input
                                {...field}
                                placeholder="Введіть назву скрипта"
                                maxLength={255}
                                showCount
                                disabled={loading}
                            />
                        </Form.Item>
                    )}
                />

                <Controller
                    name="type"
                    control={control}
                    render={({ field }) => (
                        <Form.Item
                            label="Тип"
                            validateStatus={errors.type ? 'error' : ''}
                            help={errors.type?.message}
                        >
                            <Select
                                {...field}
                                placeholder="Виберіть тип скрипта"
                                disabled={loading}
                                options={[
                                    { label: 'Скрипт', value: 'default' },
                                    { label: 'Крос-сейл', value: 'cross-sale' },
                                ]}
                            />
                        </Form.Item>
                    )}
                />

                {watchedType === 'cross-sale' && projectId && (
                    <Form.Item>
                        <Flex vertical gap={8}>
                            <Checkbox
                                checked={addToAllScripts}
                                onChange={(e) => setAddToAllScripts(e.target.checked)}
                                disabled={loading}
                            >
                                Додати в усі скрипти проекту
                            </Checkbox>
                            {initData && (
                                <Checkbox
                                    checked={removeFromAllScripts}
                                    onChange={(e) => setRemoveFromAllScripts(e.target.checked)}
                                    disabled={loading}
                                >
                                    Прибрати зі всіх скриптів проекту
                                </Checkbox>
                            )}
                        </Flex>
                    </Form.Item>
                )}

                <Flex gap="middle">
                    <Controller
                        name="product_price"
                        control={control}
                        render={({ field }) => (
                            <Form.Item
                                label="Ціна"
                                validateStatus={errors.product_price ? 'error' : ''}
                                help={errors.product_price?.message}
                                style={{ flex: 1 }}
                            >
                                <Input
                                    {...field}
                                    value={field.value || ''}
                                    placeholder="Введіть ціну"
                                    maxLength={255}
                                    disabled={loading}
                                    style={{ width: '100%' }}
                                />
                            </Form.Item>
                        )}
                    />
                </Flex>

                <Controller
                    name="product_id"
                    control={control}
                    render={({ field }) => (
                        <Form.Item
                            label="ID продукту"
                            validateStatus={errors.product_id ? 'error' : ''}
                            help={errors.product_id?.message}
                            style={{ flex: 1 }}
                            tooltip="Введіть ID товарів, через кому без пробілів"
                        >
                            <Input.TextArea
                                {...field}
                                value={field.value || ''}
                                placeholder="Введіть ID товарів"
                                maxLength={2000}
                                disabled={loading}
                                rows={6}
                            />
                        </Form.Item>
                    )}
                />

                <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                        <Form.Item
                            label="ВАЖЛИВО ⚠️"
                            validateStatus={errors.description ? 'error' : ''}
                            help={errors.description?.message}
                        >
                            {/*<TextEditorWarning />*/}
                            <TextEditor
                                value={field.value || ''}
                                onChange={field.onChange}
                                disabled={loading}
                                editorMinHeightPx={320}
                                onImageUpload={initData && projectId && scriptId ? handleInstructionImageUpload : undefined}
                            />
                        </Form.Item>
                    )}
                />

                <Controller
                    name="is_active"
                    control={control}
                    render={({ field }) => (
                        <Form.Item
                            label="Скрипт активний"
                            validateStatus={errors.is_active ? 'error' : ''}
                            help={errors.is_active?.message}
                        >
                            <Switch
                                checked={field.value}
                                onChange={field.onChange}
                                disabled={loading}
                                checkedChildren="Увімкнено"
                                unCheckedChildren="Вимкнено"
                            />
                        </Form.Item>
                    )}
                />

                <Form.Item label="Зображення">
                    <Upload
                        accept="image/*"
                        listType="picture-card"
                        multiple
                        maxCount={10}
                        beforeUpload={() => false}
                        onChange={handleGalleryChange}
                        disabled={loading}
                        fileList={[
                            ...(initData?.main_image && !deletedGalleryIds.includes(initData.main_image.id) ? [{
                                uid: initData.main_image.id,
                                name: 'main_image',
                                status: 'done' as const,
                                url: initData.main_image.url,
                            }] : []),
                            ...(initData?.gallery
                                ?.filter((img) => !deletedGalleryIds.includes(img.id))
                                .map((img) => ({
                                    uid: img.id,
                                    name: `gallery_${img.id}`,
                                    status: 'done' as const,
                                    url: img.url,
                                })) || []),
                            ...galleryFiles.map((file, index) => ({
                                uid: `-${index}`,
                                name: file.name,
                                status: 'done' as const,
                                url: URL.createObjectURL(file),
                                originFileObj: file as any,
                            })),
                        ]}
                        onRemove={(file) => {
                            handleGalleryRemove(file);
                        }}
                        itemRender={(originNode, file) => {
                            const isMainImage = file.uid === initData?.main_image?.id;
                            const isExistingGallery = initData?.gallery?.some(img => img.id === file.uid);
                            return (
                                <div style={{ position: 'relative', display: 'inline-block' }}>
                                    {originNode}
                                    <Tooltip title={isMainImage ? 'Обкладинка' : 'Встановити як обкладинку'} placement="bottom">
                                        <button
                                            type="button"
                                            onClick={isExistingGallery ? () => handleSetMain(file.uid) : undefined}
                                            style={{
                                                position: 'absolute',
                                                top: 4,
                                                left: 4,
                                                width: 22,
                                                height: 22,
                                                borderRadius: 4,
                                                border: 'none',
                                                background: 'rgba(0,0,0,0.55)',
                                                color: isMainImage ? '#faad14' : 'rgba(255,255,255,0.6)',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: isExistingGallery ? 'pointer' : 'default',
                                                zIndex: 20,
                                                padding: 0,
                                                fontSize: 13,
                                                pointerEvents: isExistingGallery || isMainImage ? 'auto' : 'none',
                                            }}
                                        >
                                            {isMainImage
                                                ? <StarFilled />
                                                : <StarOutlined />
                                            }
                                        </button>
                                    </Tooltip>
                                </div>
                            );
                        }}
                    >
                        {((initData?.main_image && !deletedGalleryIds.includes(initData.main_image.id) ? 1 : 0)
                            + (initData?.gallery?.filter((img) => !deletedGalleryIds.includes(img.id)).length || 0)
                            + galleryFiles.length < 10) && (
                            <div>
                                <PlusOutlined />
                                <div style={{ marginTop: 8 }}>Завантажити</div>
                            </div>
                        )}
                    </Upload>
                </Form.Item>

                {projectId && onOpenCrossSellsModal && (
                    <Card
                        size="small"
                        title="Крос-сейли"
                        style={{
                            marginBottom: 24,
                            borderColor: token.colorPrimary,
                            background: token.colorPrimaryBg,
                        }}
                    >
                        <Flex justify="center">
                            <Tooltip title={isCreating || !scriptId ? 'Спочатку створіть скрипт' : 'Обрати та впорядкувати крос-сейли для цього скрипта'}>
                                <Button
                                    type="primary"
                                    size="large"
                                    icon={<ShoppingCartOutlined />}
                                    onClick={onOpenCrossSellsModal}
                                    disabled={loading || isCreating || !scriptId}
                                    style={{
                                        minWidth: 280,
                                        height: 'auto',
                                        padding: '10px 28px',
                                        background: token.colorWarning,
                                        borderColor: token.colorWarning,
                                        color: token.colorTextLightSolid,
                                    }}
                                >
                                    Додати крос-сейли
                                </Button>
                            </Tooltip>
                        </Flex>
                    </Card>
                )}

                <EditableFieldsArray
                    control={control}
                    errors={errors}
                    fields={fields}
                    remove={remove}
                    append={append}
                    loading={loading}
                    label="Розділи"
                    instructionImageUpload={initData && projectId && scriptId ? handleInstructionImageUpload : undefined}
                    onLoadDefaults={resolvedDefaultInstructions.length > 0 ? handleLoadDefaultInstructions : undefined}
                    bulkUpdateIndices={initData && projectId ? bulkUpdateIndices : undefined}
                    onToggleBulkUpdate={initData && projectId ? (index) => {
                        setBulkUpdateIndices((prev) => {
                            const next = new Set(prev);
                            next.has(index) ? next.delete(index) : next.add(index);
                            return next;
                        });
                    } : undefined}
                />

                <FormPrimaryActionsRow
                    onCancel={onCancel}
                    cancelButtonText={cancelButtonText}
                    cancelTo={cancelTo}
                    loading={loading}
                    submitDisabled={primaryDisabled}
                    submitLabel={submitLabel}
                    style={{ marginTop: 24 }}
                />
            </Form>

            <FormSaveFloatButton
                formDomId={SCRIPT_FORM_DOM_ID}
                hiddenSubmitRef={hiddenSubmitRef}
                loading={loading}
                submitDisabled={primaryDisabled}
                submitLabel={submitLabel}
            />

            <ConfirmModal
                open={deleteMediaModalOpen}
                mode="update"
                entityType="script"
                entityName="зображення"
                onConfirm={handleConfirmDeleteMedia}
                onCancel={handleCancelDeleteMedia}
                loading={isDeletingMedia}
            />
        </Spin>
    );
});
